import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchConfiguredSites,
  browseSharePointFiles,
  syncSharePointFiles,
  syncAllSharePointFiles,
  unsyncAllSharePointFiles,
  clearBrowseItems,
  clearSyncResults,
  setSelectedSite,
  clearSiteSelection,
  fetchAutoSyncFolders,
  addAutoSyncFolder,
  removeAutoSyncFolder,
  updateAutoSyncFolder,
  clearAutoSyncFolders,
  fetchAuditLogs,
  fetchAutoSyncFolderFilesCount,
  clearAutoSyncFilesCount,
  triggerDeltaSync,
} from '../../../slice/sharepointSlice'
import { fetchDefaultFiles } from '../../../slice/defaultFilesSlice'
import { Loader2, Folder, FileText, ChevronRight, Check, Globe, ArrowLeft, RefreshCw, X, History } from 'lucide-react'

const SharePointFileBrowser = ({ isOpen, onClose, organizationId, taskId, userOrganizationId }) => {
  const dispatch = useDispatch()
  const {
    configuredSites,
    configuredSitesLoading,
    configuredSitesError,
    selectedSite,
    browseItems,
    browsePath,
    browseLoading,
    browseError,
    syncLoading,
    syncResults,
    autoSyncFolders,
    addAutoSyncLoading,
    auditLogs,
    auditLogsLoading,
    removeAutoSyncLoading,
    autoSyncFilesCount,
    autoSyncFilesCountLoading,
    deltaSyncLoading,
    deltaSyncResults,
    syncAllLoading,
    syncAllResults,
    syncAllError,
    unsyncAllLoading,
    unsyncAllError,
  } = useSelector((state) => state.sharepoint)

  const [pathStack, setPathStack] = useState(['/'])
  const [selectedItems, setSelectedItems] = useState({})
  const [autoSyncPopover, setAutoSyncPopover] = useState(null) // { item, position }
  const [autoSyncImporting, setAutoSyncImporting] = useState(false)
  const [autoSyncResults, setAutoSyncResults] = useState(null)
  const [showAutoSyncList, setShowAutoSyncList] = useState(false)
  const [showAuditLog, setShowAuditLog] = useState(false)
  const [removeConfirmDialog, setRemoveConfirmDialog] = useState(null) // { folder }
  const [deleteFilesChecked, setDeleteFilesChecked] = useState(false)
  const [unsyncAllSite, setUnsyncAllSite] = useState(null) // site to confirm un-sync-all for
  const [showSyncAllResults, setShowSyncAllResults] = useState(false)
  const [syncAllSite, setSyncAllSite] = useState(null) // site being synced

  useEffect(() => {
    if (isOpen && organizationId) {
      dispatch(clearSiteSelection())
      dispatch(clearSyncResults())
      setPathStack(['/'])
      setSelectedItems({})
      dispatch(fetchConfiguredSites(organizationId))
    }
  }, [isOpen, organizationId, dispatch])

  // When a site is selected, browse its root and fetch auto-sync folders
  useEffect(() => {
    if (selectedSite && organizationId) {
      setPathStack(['/'])
      setSelectedItems({})
      dispatch(browseSharePointFiles({ orgId: organizationId, siteId: selectedSite.id, path: '/', taskId }))
      dispatch(fetchAutoSyncFolders({ orgId: organizationId, siteId: selectedSite.id, taskId: taskId }))
    }
  }, [selectedSite, organizationId, taskId, dispatch])

  if (!isOpen) return null

  const currentPath = pathStack[pathStack.length - 1]

  const handleSelectSite = (site) => {
    dispatch(setSelectedSite(site))
  }

  const handleBackToSites = () => {
    dispatch(clearSiteSelection())
    dispatch(clearBrowseItems())
  }

  const navigateToFolder = (folderName) => {
    const newPath = currentPath === '/' ? `/${folderName}` : `${currentPath}/${folderName}`
    setPathStack([...pathStack, newPath])
    dispatch(browseSharePointFiles({ orgId: organizationId, siteId: selectedSite.id, path: newPath, taskId }))
  }

  const navigateBack = (index) => {
    const newStack = pathStack.slice(0, index + 1)
    setPathStack(newStack)
    dispatch(browseSharePointFiles({ orgId: organizationId, siteId: selectedSite.id, path: newStack[newStack.length - 1], taskId }))
  }

  const toggleItem = (item) => {
    setSelectedItems(prev => {
      const copy = { ...prev }
      if (copy[item.id]) {
        delete copy[item.id]
      } else {
        copy[item.id] = item
      }
      return copy
    })
  }

  const handleImport = async () => {
    const items = Object.values(selectedItems).map(item => ({
      item_id: item.id,
      file_name: item.name,
      file_path: currentPath === '/' ? `/${item.name}` : `${currentPath}/${item.name}`,
      etag: item.eTag || null,
      last_modified: item.lastModifiedDateTime || null,
    }))

    try {
      await dispatch(syncSharePointFiles({
        org_id: organizationId,
        task_id: taskId,
        site_id: selectedSite.id,
        items,
      })).unwrap()

      // Refresh default files after sync
      dispatch(fetchDefaultFiles({
        organizationId: userOrganizationId,
        taskId: taskId,
      }))
    } catch (err) {
      // Error handled by Redux state
    }
  }

  const handleClose = () => {
    dispatch(clearSiteSelection())
    dispatch(clearSyncResults())
    dispatch(clearAutoSyncFolders())
    onClose()
  }

  // Auto-sync folder helpers
  const getFolderPath = (folderName) => {
    return currentPath === '/' ? `/${folderName}` : `${currentPath}/${folderName}`
  }

  const getAutoSyncFolder = (folderPath) => {
    return autoSyncFolders.find(f => f.folder_path === folderPath)
  }

  const isAutoSyncEnabled = (folderPath) => {
    return !!getAutoSyncFolder(folderPath)
  }

  const handleSyncIconClick = (item, e) => {
    e.stopPropagation()
    const folderPath = getFolderPath(item.name)
    const existingFolder = getAutoSyncFolder(folderPath)
    const rect = e.currentTarget.getBoundingClientRect()

    setAutoSyncPopover({
      item,
      folderPath,
      existingFolder,
      position: { top: rect.bottom + 4, left: rect.left }
    })
  }

  const confirmAutoSync = async (includeSubfolders) => {
    if (!autoSyncPopover) return

    setAutoSyncPopover(null)
    setAutoSyncImporting(true)

    try {
      const result = await dispatch(addAutoSyncFolder({
        siteId: selectedSite.id,
        taskId: taskId,
        folderPath: autoSyncPopover.folderPath,
        folderItemId: autoSyncPopover.item.id,
        includeSubfolders,
        importExisting: true,
      })).unwrap()

      // Show results if files were imported
      if (result.import_results && result.import_results.length > 0) {
        setAutoSyncResults(result)
        // Refresh default files
        dispatch(fetchDefaultFiles({
          organizationId: userOrganizationId,
          taskId: taskId,
        }))
      }
    } catch (err) {
      // Error handled by Redux
    } finally {
      setAutoSyncImporting(false)
    }
  }

  const handleUpdateSubfolders = (includeSubfolders) => {
    if (!autoSyncPopover?.existingFolder) return

    dispatch(updateAutoSyncFolder({
      folderId: autoSyncPopover.existingFolder.id,
      includeSubfolders,
    }))
    setAutoSyncPopover(null)
  }

  const handleDisableAutoSync = () => {
    if (!autoSyncPopover?.existingFolder) return

    // Show confirmation dialog and fetch files count
    setRemoveConfirmDialog({ folder: autoSyncPopover.existingFolder })
    setDeleteFilesChecked(false)
    dispatch(fetchAutoSyncFolderFilesCount(autoSyncPopover.existingFolder.id))
    setAutoSyncPopover(null)
  }

  const handleShowRemoveConfirm = (folder) => {
    setRemoveConfirmDialog({ folder })
    setDeleteFilesChecked(false)
    dispatch(fetchAutoSyncFolderFilesCount(folder.id))
  }

  const handleConfirmRemove = async () => {
    if (!removeConfirmDialog?.folder) return

    const shouldDeleteFiles = deleteFilesChecked

    try {
      await dispatch(removeAutoSyncFolder({
        folderId: removeConfirmDialog.folder.id,
        deleteFiles: shouldDeleteFiles
      })).unwrap()

      // Refresh default files if files were deleted
      if (shouldDeleteFiles) {
        await dispatch(fetchDefaultFiles({ organizationId, taskId }))
      }
    } catch (error) {
      console.error('Failed to remove auto-sync folder:', error)
    } finally {
      // Only close dialog after everything is done
      setRemoveConfirmDialog(null)
      setDeleteFilesChecked(false)
      dispatch(clearAutoSyncFilesCount())
    }
  }

  const handleCancelRemove = () => {
    setRemoveConfirmDialog(null)
    setDeleteFilesChecked(false)
    dispatch(clearAutoSyncFilesCount())
  }

  const closePopover = () => {
    setAutoSyncPopover(null)
  }

  const includesSubfolders = (folderPath) => {
    const folder = getAutoSyncFolder(folderPath)
    return folder ? folder.include_subfolders : true
  }

  const handleSyncAll = async (site) => {
    setSyncAllSite(site)
    try {
      await dispatch(syncAllSharePointFiles({
        org_id: organizationId,
        task_id: taskId,
        site_id: site.id,
      })).unwrap()
      setShowSyncAllResults(true)
      dispatch(fetchDefaultFiles({ organizationId: userOrganizationId, taskId }))
    } catch (err) {
      setShowSyncAllResults(true)
    }
  }

  const handleUnsyncAll = async () => {
    if (!unsyncAllSite) return
    try {
      await dispatch(unsyncAllSharePointFiles({
        org_id: organizationId,
        task_id: taskId,
        site_id: unsyncAllSite.id,
      })).unwrap()
      dispatch(fetchDefaultFiles({ organizationId: userOrganizationId, taskId }))
    } catch (err) {
      // error shown via unsyncAllError
    } finally {
      setUnsyncAllSite(null)
    }
  }

  const selectedCount = Object.keys(selectedItems).length
  // TEMPORARILY DISABLED - folder/subfolder auto-sync UI.
  // Force the count to 0 so all UI gated on `autoSyncCount > 0` (footer button,
  // folder list popover, etc.) hides, regardless of any leftover DB rows.
  const autoSyncCount = 0
  // Original: const autoSyncCount = autoSyncFolders.length

  const formatSize = (bytes) => {
    if (!bytes) return '-'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }

  // Sync All results screen
  if (showSyncAllResults) {
    const synced = syncAllResults?.results?.filter(r => r.status === 'synced') || []
    const skipped = syncAllResults?.results?.filter(r => r.status === 'skipped') || []
    const errors = syncAllResults?.results?.filter(r => r.status === 'error') || []
    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120]">
        <div className="bg-white rounded-lg p-6 w-[560px] max-h-[80vh] overflow-y-auto">
          <h3 className="text-lg font-semibold mb-4">Sync All abgeschlossen</h3>
          {syncAllError ? (
            <p className="text-sm text-red-600 mb-4">{syncAllError}</p>
          ) : (
            <p className="text-sm text-gray-600 mb-4">
              {syncAllResults?.message}
            </p>
          )}
          {synced.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-green-700 mb-2">Erfolgreich importiert ({synced.length}):</h4>
              <ul className="space-y-1 max-h-40 overflow-y-auto">
                {synced.map((r, i) => (
                  <li key={i} className="flex items-center text-sm text-green-600">
                    <Check className="w-4 h-4 mr-2 flex-shrink-0" />
                    {r.file_name} {r.page_count ? `(${r.page_count} Seiten)` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {skipped.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-600 mb-2">Übersprungen ({skipped.length}):</h4>
              <ul className="space-y-1 max-h-32 overflow-y-auto">
                {skipped.map((r, i) => (
                  <li key={i} className="flex items-center text-sm text-gray-500">
                    <FileText className="w-4 h-4 mr-2 flex-shrink-0" />
                    {r.file_name}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {errors.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-red-700 mb-2">Fehler ({errors.length}):</h4>
              <ul className="space-y-1 max-h-32 overflow-y-auto">
                {errors.map((r, i) => (
                  <li key={i} className="text-sm text-red-600">{r.file_name}: {r.error}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex justify-end">
            <button
              onClick={() => { setShowSyncAllResults(false); setSyncAllSite(null) }}
              className="px-4 py-2 text-sm font-medium text-white bg-custom-yellow rounded-md hover:bg-yellow-500"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Sync All loading overlay (full-screen while in progress from site selection)
  if (syncAllLoading && syncAllSite && !selectedSite) {
    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120]">
        <div className="bg-white rounded-lg p-8 w-[400px] text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-custom-yellow" />
          <div className="text-base font-medium text-gray-800 mb-1">Sync All läuft...</div>
          <div className="text-sm text-gray-500">Alle Dateien von <strong>{syncAllSite.site_name}</strong> werden importiert.</div>
          <div className="text-xs text-gray-400 mt-2">Dies kann einige Minuten dauern.</div>
        </div>
      </div>
    )
  }

  // Un-sync All confirmation dialog
  if (unsyncAllSite) {
    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120]">
        <div className="bg-white rounded-lg p-6 w-[440px]">
          <h3 className="text-lg font-semibold mb-3">Un-sync All bestätigen</h3>
          <p className="text-sm text-gray-600 mb-4">
            Alle aus <strong>{unsyncAllSite.site_name}</strong> importierten Dateien werden entfernt (Embeddings, Dateieinträge und Mappings). Diese Aktion kann nicht rückgängig gemacht werden.
          </p>
          {unsyncAllError && (
            <p className="text-sm text-red-600 mb-3">{unsyncAllError}</p>
          )}
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setUnsyncAllSite(null)}
              disabled={unsyncAllLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              onClick={handleUnsyncAll}
              disabled={unsyncAllLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
            >
              {unsyncAllLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" />Entferne...</>
              ) : (
                'Alle entfernen'
              )}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // If auto-sync folder import completed, show results
  if (autoSyncResults) {
    const synced = autoSyncResults.import_results?.filter(r => r.status === 'synced') || []
    const skipped = autoSyncResults.import_results?.filter(r => r.status === 'skipped') || []
    const errors = autoSyncResults.import_results?.filter(r => r.status === 'error') || []

    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120]">
        <div className="bg-white rounded-lg p-6 w-[560px] max-h-[80vh] overflow-y-auto">
          <h3 className="text-lg font-semibold mb-4">Auto-Sync aktiviert</h3>
          <p className="text-sm text-gray-600 mb-4">
            {synced.length} Datei(en) importiert{skipped.length > 0 ? `, ${skipped.length} übersprungen` : ''}{errors.length > 0 ? `, ${errors.length} fehlgeschlagen` : ''}
          </p>

          {synced.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-green-700 mb-2">Erfolgreich importiert:</h4>
              <ul className="space-y-1 max-h-40 overflow-y-auto">
                {synced.map((r, i) => (
                  <li key={i} className="flex items-center text-sm text-green-600">
                    <Check className="w-4 h-4 mr-2 flex-shrink-0" />
                    {r.file_name} ({r.page_count} Seiten)
                  </li>
                ))}
              </ul>
            </div>
          )}

          {skipped.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-600 mb-2">Übersprungen:</h4>
              <ul className="space-y-1 max-h-40 overflow-y-auto">
                {skipped.map((r, i) => (
                  <li key={i} className="flex items-center text-sm text-gray-500">
                    <FileText className="w-4 h-4 mr-2 flex-shrink-0" />
                    {r.file_name}{r.reason ? ` — ${r.reason}` : ' (bereits importiert)'}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {errors.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-red-700 mb-2">Fehler:</h4>
              <ul className="space-y-1 max-h-40 overflow-y-auto">
                {errors.map((r, i) => (
                  <li key={i} className="text-sm text-red-600">
                    {r.file_name}: {r.error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {synced.length === 0 && skipped.length === 0 && errors.length === 0 && (
            <p className="text-sm text-gray-500 mb-4">
              Keine Dateien im Ordner gefunden. Neue Dateien werden automatisch importiert.
            </p>
          )}

          <div className="flex justify-end">
            <button
              onClick={() => setAutoSyncResults(null)}
              className="px-4 py-2 text-sm font-medium text-white bg-custom-yellow rounded-md hover:bg-yellow-500"
            >
              Weiter
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Remove auto-sync confirmation dialog
  if (removeConfirmDialog) {
    const filesCount = autoSyncFilesCount?.count || 0
    const filesList = autoSyncFilesCount?.files || []

    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120]">
        <div className="bg-white rounded-lg p-6 w-[480px] max-h-[80vh] overflow-y-auto">
          <h3 className="text-lg font-semibold mb-4">Auto-Sync entfernen</h3>
          <p className="text-sm text-gray-600 mb-4">
            Möchten Sie Auto-Sync für diesen Ordner wirklich deaktivieren?
          </p>
          <div className="bg-gray-50 rounded p-3 mb-4">
            <div className="flex items-center">
              <Folder className="w-5 h-5 text-yellow-500 mr-2" />
              <div>
                <div className="font-medium text-sm">
                  {removeConfirmDialog.folder.folder_path.split('/').pop() || 'Root'}
                </div>
                <div className="text-xs text-gray-500">{removeConfirmDialog.folder.folder_path}</div>
              </div>
            </div>
          </div>

          {autoSyncFilesCountLoading ? (
            <div className="flex items-center text-sm text-gray-500 mb-4">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Lade Datei-Informationen...
            </div>
          ) : filesCount > 0 ? (
            <div className="mb-4">
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={deleteFilesChecked}
                  onChange={(e) => setDeleteFilesChecked(e.target.checked)}
                  className="mt-1 mr-3 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    Auch {filesCount} automatisch importierte Datei{filesCount !== 1 ? 'en' : ''} löschen
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    Manuell importierte Dateien bleiben erhalten.
                  </p>
                </div>
              </label>
              {deleteFilesChecked && filesList.length > 0 && (
                <div className="mt-3 ml-7 p-2 bg-red-50 rounded max-h-32 overflow-y-auto">
                  <div className="text-xs font-medium text-red-700 mb-1">Folgende Dateien werden gelöscht:</div>
                  <ul className="space-y-0.5">
                    {filesList.map((f, i) => (
                      <li key={i} className="text-xs text-red-600 flex items-center">
                        <FileText className="w-3 h-3 mr-1 flex-shrink-0" />
                        {f.filename}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500 mb-4">
              Keine automatisch importierten Dateien gefunden.
            </p>
          )}

          <div className="flex justify-end space-x-3">
            <button
              onClick={handleCancelRemove}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              disabled={removeAutoSyncLoading}
            >
              Abbrechen
            </button>
            <button
              onClick={handleConfirmRemove}
              disabled={removeAutoSyncLoading}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md flex items-center ${
                deleteFilesChecked
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-custom-yellow hover:bg-yellow-500'
              } disabled:opacity-50`}
            >
              {removeAutoSyncLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Entferne...</>
              ) : deleteFilesChecked ? (
                'Entfernen und Dateien löschen'
              ) : (
                'Nur Auto-Sync entfernen'
              )}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // If sync completed, show results
  if (syncResults) {
    const synced = syncResults.results?.filter(r => r.status === 'synced') || []
    const errors = syncResults.results?.filter(r => r.status === 'error') || []
    const skipped = syncResults.results?.filter(r => r.status === 'skipped') || []

    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120]">
        <div className="bg-white rounded-lg p-6 w-[560px] max-h-[80vh] overflow-y-auto">
          <h3 className="text-lg font-semibold mb-4">Import abgeschlossen</h3>
          <p className="text-sm text-gray-600 mb-4">{syncResults.message}</p>

          {synced.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-green-700 mb-2">Erfolgreich importiert:</h4>
              <ul className="space-y-1">
                {synced.map((r, i) => (
                  <li key={i} className="flex items-center text-sm text-green-600">
                    <Check className="w-4 h-4 mr-2" />
                    {r.file_name} ({r.page_count} Seiten)
                  </li>
                ))}
              </ul>
            </div>
          )}

          {skipped.length > 0 && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded">
              <h4 className="text-sm font-medium text-amber-700 mb-2">Übersprungen:</h4>
              <ul className="space-y-1">
                {skipped.map((r, i) => (
                  <li key={i} className="text-sm text-amber-700">
                    {r.file_name}: {r.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {errors.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-red-700 mb-2">Fehler:</h4>
              <ul className="space-y-1">
                {errors.map((r, i) => (
                  <li key={i} className="text-sm text-red-600">
                    {r.file_name}: {r.error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-white bg-custom-yellow rounded-md hover:bg-yellow-500"
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Step 1: Site Selection (from configured sites)
  if (!selectedSite) {
    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120]">
        <div className="bg-white rounded-lg p-6 w-[560px] max-h-[80vh] flex flex-col">
          <h3 className="text-lg font-semibold mb-2">SharePoint-Site auswählen</h3>
          <p className="text-sm text-gray-500 mb-4">Wählen Sie eine konfigurierte Site aus, von der Sie Dateien importieren möchten.</p>

          {configuredSitesError && (
            <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {configuredSitesError}
            </div>
          )}

          <div className="flex-1 overflow-y-auto border border-gray-200 rounded mb-4 min-h-[300px]">
            {configuredSitesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
                <span className="text-sm text-gray-500">Sites werden geladen...</span>
              </div>
            ) : configuredSites.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400 text-sm">
                <Globe className="w-8 h-8 mb-2 text-gray-300" />
                <p>Keine SharePoint-Sites konfiguriert</p>
                <p className="text-xs mt-1">Konfigurieren Sie Sites in den Organisationseinstellungen</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {configuredSites.map((site) => (
                  <div
                    key={site.id}
                    className="flex items-center p-4 hover:bg-gray-50"
                  >
                    <Globe className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => handleSelectSite(site)}
                    >
                      <div className="font-medium text-gray-900">{site.site_name}</div>
                      <div className="text-sm text-gray-500 truncate">{site.site_url}</div>
                    </div>
                    <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleSyncAll(site) }}
                        disabled={syncAllLoading}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-custom-yellow rounded hover:bg-yellow-500 disabled:opacity-50 flex items-center gap-1"
                        title="Alle Dateien dieser Site importieren"
                      >
                        {syncAllLoading && syncAllSite?.id === site.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <RefreshCw className="w-3 h-3" />
                        )}
                        Sync All
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setUnsyncAllSite(site) }}
                        disabled={unsyncAllLoading}
                        className="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-300 rounded hover:bg-red-50 disabled:opacity-50 flex items-center gap-1"
                        title="Alle importierten Dateien dieser Site entfernen"
                      >
                        <X className="w-3 h-3" />
                        Un-sync All
                      </button>
                      <button
                        onClick={() => handleSelectSite(site)}
                        className="p-1.5 hover:bg-gray-200 rounded"
                        title="Dateien durchsuchen"
                      >
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Abbrechen
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Step 2: File Browser (no drive selection needed - drive_id is stored in site record)
  const breadcrumbs = pathStack.map((p, i) => ({
    label: i === 0 ? 'Root' : p.split('/').pop(),
    index: i,
  }))

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120]">
      <div className="bg-white rounded-lg p-6 w-[640px] max-h-[80vh] flex flex-col relative">
        <div className="flex items-center mb-2">
          <button onClick={handleBackToSites} className="mr-2 p-1 hover:bg-gray-100 rounded">
            <ArrowLeft className="w-5 h-5 text-gray-500" />
          </button>
          <h3 className="text-lg font-semibold">Von SharePoint importieren</h3>
        </div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-gray-500">
            {selectedSite.site_name}
          </p>
        </div>

        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-4 overflow-x-auto">
          {breadcrumbs.map((b, i) => (
            <span key={i} className="flex items-center">
              {i > 0 && <ChevronRight className="w-3 h-3 mx-1 text-gray-400" />}
              <button
                onClick={() => navigateBack(b.index)}
                className={`hover:text-blue-600 ${
                  i === breadcrumbs.length - 1 ? 'font-medium text-gray-900' : ''
                }`}
              >
                {b.label}
              </button>
            </span>
          ))}
        </div>

        {browseError && (
          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {browseError}
          </div>
        )}

        {/* Auto-sync import loading overlay */}
        {autoSyncImporting && (
          <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-40 rounded-lg">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-custom-yellow" />
              <div className="text-sm font-medium text-gray-700">Ordner wird synchronisiert...</div>
              <div className="text-xs text-gray-500 mt-1">Dateien werden importiert</div>
            </div>
          </div>
        )}

        {/* File list */}
        <div className="flex-1 overflow-y-auto border border-gray-200 rounded mb-4 min-h-[300px]">
          {browseLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span className="text-sm text-gray-500">Laden...</span>
            </div>
          ) : browseItems.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-gray-400 text-sm">
              Keine Dateien in diesem Ordner
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="w-10 px-3 py-2"></th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Größe</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Geändert</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[...browseItems]
                  .sort((a, b) => {
                    // Folders first, then files
                    if (a.type === 'folder' && b.type !== 'folder') return -1
                    if (a.type !== 'folder' && b.type === 'folder') return 1
                    return a.name.localeCompare(b.name)
                  })
                  .map((item) => {
                    const folderPath = item.type === 'folder' ? getFolderPath(item.name) : null
                    // TEMPORARILY DISABLED - folder/subfolder auto-sync indicator.
                    const isAutoSync = false
                    // Original: item.type === 'folder' && isAutoSyncEnabled(folderPath)
                    const isImportedFile = item.type !== 'folder' && !!item.is_imported

                    return (
                      <tr
                        key={item.id}
                        className={`hover:bg-gray-50 cursor-pointer ${
                          selectedItems[item.id] ? 'bg-blue-50' : ''
                        } ${isAutoSync || isImportedFile ? 'bg-green-50' : ''}`}
                        onClick={() => {
                          if (item.type === 'folder') {
                            navigateToFolder(item.name)
                          } else {
                            toggleItem(item)
                          }
                        }}
                      >
                        <td className="px-3 py-2 text-center">
                          {item.type === 'folder' ? (
                            // TEMPORARILY DISABLED - Auto-sync button
                            // <button
                            //   onClick={(e) => handleSyncIconClick(item, e)}
                            //   disabled={addAutoSyncLoading}
                            //   className={`p-1 rounded transition-colors ${
                            //     isAutoSync
                            //       ? 'bg-green-100 text-green-600 hover:bg-green-200'
                            //       : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                            //   }`}
                            //   title={isAutoSync ? "Auto-Sync deaktivieren" : "Auto-Sync aktivieren"}
                            // >
                            //   <RefreshCw className={`w-4 h-4 ${addAutoSyncLoading ? 'animate-spin' : ''}`} />
                            // </button>
                            <span className="text-gray-300 text-xs">-</span>
                          ) : (
                            <input
                              type="checkbox"
                              checked={!!selectedItems[item.id]}
                              onChange={() => toggleItem(item)}
                              onClick={(e) => e.stopPropagation()}
                              className="rounded border-gray-300"
                            />
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center">
                            {item.type === 'folder' ? (
                              <Folder className="w-4 h-4 text-yellow-500 mr-2" />
                            ) : (
                              <FileText className="w-4 h-4 text-gray-400 mr-2" />
                            )}
                            <span className={item.type === 'folder' ? 'font-medium text-blue-600' : ''}>
                              {item.name}
                            </span>
                            {item.type === 'folder' && (
                              <ChevronRight className="w-3 h-3 ml-1 text-gray-400" />
                            )}
                            {isAutoSync && (
                              <span className="ml-2 text-xs text-green-600 bg-green-100 px-1.5 py-0.5 rounded">
                                {includesSubfolders(folderPath) ? 'Auto-Sync (+Unterordner)' : 'Auto-Sync'}
                              </span>
                            )}
                            {isImportedFile && (
                              <span className="ml-2 text-xs text-green-600 bg-green-100 px-1.5 py-0.5 rounded">
                                Importiert
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-gray-500">
                          {item.type === 'file' ? formatSize(item.size) : `${item.childCount || 0} Elemente`}
                        </td>
                        <td className="px-3 py-2 text-gray-500">
                          {item.lastModifiedDateTime
                            ? new Date(item.lastModifiedDateTime).toLocaleDateString('de-DE')
                            : '-'
                          }
                        </td>
                      </tr>
                    )
                  })}
              </tbody>
            </table>
          )}
        </div>

        {/* TEMPORARILY DISABLED - Auto-sync popover (folder/subfolder sync) */}
        {false && autoSyncPopover && (
          <>
            {/* Backdrop to close popover */}
            <div
              className="fixed inset-0 z-50"
              onClick={closePopover}
            />
            {/* Popover */}
            <div
              className="fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-3 w-64"
              style={{
                top: autoSyncPopover.position.top,
                left: autoSyncPopover.position.left
              }}
            >
              <div className="text-sm font-medium text-gray-900 mb-2">
                {autoSyncPopover.existingFolder ? 'Auto-Sync bearbeiten' : 'Auto-Sync aktivieren'}
              </div>
              <div className="text-xs text-gray-500 mb-3">
                {autoSyncPopover.item.name}
              </div>

              {autoSyncPopover.existingFolder ? (
                // Edit existing auto-sync
                <div className="space-y-2">
                  <button
                    onClick={() => handleUpdateSubfolders(true)}
                    disabled={autoSyncPopover.existingFolder.include_subfolders === true}
                    className={`w-full text-left px-3 py-2 text-sm rounded flex items-center ${
                      autoSyncPopover.existingFolder.include_subfolders
                        ? 'bg-green-50 text-green-700'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <Folder className="w-4 h-4 text-yellow-500 mr-2" />
                    <div>
                      <div className="font-medium">Mit Unterordnern</div>
                      {autoSyncPopover.existingFolder.include_subfolders && (
                        <div className="text-xs text-green-600">Aktiv</div>
                      )}
                    </div>
                  </button>
                  <button
                    onClick={() => handleUpdateSubfolders(false)}
                    disabled={autoSyncPopover.existingFolder.include_subfolders === false}
                    className={`w-full text-left px-3 py-2 text-sm rounded flex items-center ${
                      !autoSyncPopover.existingFolder.include_subfolders
                        ? 'bg-green-50 text-green-700'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <Folder className="w-4 h-4 text-gray-400 mr-2" />
                    <div>
                      <div className="font-medium">Nur dieser Ordner</div>
                      {!autoSyncPopover.existingFolder.include_subfolders && (
                        <div className="text-xs text-green-600">Aktiv</div>
                      )}
                    </div>
                  </button>
                  <hr className="my-2" />
                  <button
                    onClick={handleDisableAutoSync}
                    className="w-full text-left px-3 py-2 text-sm rounded hover:bg-red-50 text-red-600 flex items-center"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    <div className="font-medium">Auto-Sync deaktivieren</div>
                  </button>
                </div>
              ) : (
                // New auto-sync
                <div className="space-y-2">
                  <button
                    onClick={() => confirmAutoSync(true)}
                    className="w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-100 flex items-center"
                  >
                    <Folder className="w-4 h-4 text-yellow-500 mr-2" />
                    <div>
                      <div className="font-medium">Mit Unterordnern</div>
                      <div className="text-xs text-gray-500">Neue Dateien in allen Unterordnern</div>
                    </div>
                  </button>
                  <button
                    onClick={() => confirmAutoSync(false)}
                    className="w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-100 flex items-center"
                  >
                    <Folder className="w-4 h-4 text-gray-400 mr-2" />
                    <div>
                      <div className="font-medium">Nur dieser Ordner</div>
                      <div className="text-xs text-gray-500">Keine Unterordner einbeziehen</div>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center relative">
          <div className="text-sm text-gray-500 space-y-1">
            <div>
              {selectedCount > 0
                ? `${selectedCount} Datei(en) zum Import ausgewählt`
                : 'Dateien zum Importieren auswählen'}
            </div>
            {autoSyncCount > 0 && (
              <button
                onClick={() => setShowAutoSyncList(!showAutoSyncList)}
                className="flex items-center text-green-600 hover:text-green-700 hover:underline cursor-pointer"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                {autoSyncCount} Ordner mit Auto-Sync
                <ChevronRight className={`w-3 h-3 ml-1 transition-transform ${showAutoSyncList ? 'rotate-90' : ''}`} />
              </button>
            )}
            <button
              onClick={() => {
                dispatch(fetchAuditLogs({ taskId }))
                setShowAuditLog(true)
              }}
              className="flex items-center text-blue-600 hover:text-blue-700 hover:underline cursor-pointer"
            >
              <History className="w-3 h-3 mr-1" />
              Sync-Protokoll
            </button>

            {/* Auto-sync folders popover */}
            {showAutoSyncList && autoSyncCount > 0 && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowAutoSyncList(false)}
                />
                <div className="absolute bottom-full left-0 mb-2 bg-white rounded-lg shadow-lg border border-gray-200 p-3 w-80 z-50 max-h-64 overflow-y-auto">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900">Auto-Sync Ordner</span>
                    <button
                      onClick={() => setShowAutoSyncList(false)}
                      className="text-gray-400 hover:text-gray-600 p-0.5"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-2">
                    {autoSyncFolders.map((folder) => (
                      <div
                        key={folder.id}
                        className="flex items-start justify-between p-2 bg-gray-50 rounded hover:bg-gray-100"
                      >
                        <div className="flex items-start min-w-0 flex-1">
                          <Folder className="w-4 h-4 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {folder.folder_path.split('/').pop() || 'Root'}
                            </div>
                            <div className="text-xs text-gray-500 truncate" title={folder.folder_path}>
                              {folder.folder_path}
                            </div>
                            <div className="text-xs text-green-600">
                              {folder.include_subfolders ? 'inkl. Unterordner' : 'nur dieser Ordner'}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleShowRemoveConfirm(folder)
                          }}
                          className="ml-2 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded flex-shrink-0"
                          title="Auto-Sync entfernen"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              disabled={syncLoading}
            >
              {selectedCount === 0 && autoSyncCount > 0 ? 'Fertig' : 'Abbrechen'}
            </button>
            {selectedCount > 0 && (
              <button
                onClick={handleImport}
                disabled={syncLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-custom-yellow rounded-md hover:bg-yellow-500 disabled:opacity-50 flex items-center"
              >
                {syncLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Importiere...</>
                ) : (
                  `Importieren (${selectedCount})`
                )}
              </button>
            )}
          </div>
        </div>

        {/* Audit Log Modal */}
        {showAuditLog && (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120]">
            <div className="bg-white rounded-lg shadow-xl w-[700px] max-h-[80vh] flex flex-col">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <History className="w-5 h-5 mr-2 text-blue-600" />
                  SharePoint Sync-Protokoll
                </h3>
                <button
                  onClick={() => setShowAuditLog(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {auditLogsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                    <span className="ml-2 text-gray-500">Lade Protokoll...</span>
                  </div>
                ) : auditLogs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Noch keine Sync-Aktivitäten vorhanden.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {auditLogs.map((log) => (
                      <div
                        key={log.id}
                        className={`p-3 rounded-lg border ${
                          log.status === 'error'
                            ? 'bg-red-50 border-red-200'
                            : log.operation === 'delete'
                            ? 'bg-orange-50 border-orange-200'
                            : 'bg-green-50 border-green-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span
                                className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  log.operation === 'import'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-orange-100 text-orange-800'
                                }`}
                              >
                                {log.operation === 'import' ? 'Import' : 'Gelöscht'}
                              </span>
                              {log.status === 'error' && (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                  Fehler
                                </span>
                              )}
                            </div>
                            <div className="mt-1 font-medium text-gray-900 truncate">
                              {log.filename}
                            </div>
                            {log.folder_path && (
                              <div className="text-xs text-gray-500 truncate" title={log.folder_path}>
                                Ordner: {log.folder_path}
                              </div>
                            )}
                            {log.sync_reason && (
                              <div className="text-xs text-gray-600">
                                {log.sync_reason}
                              </div>
                            )}
                            {log.error_message && (
                              <div className="mt-1 text-xs text-red-600">
                                {log.error_message}
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-gray-400 whitespace-nowrap ml-4">
                            {new Date(log.created_at).toLocaleString('de-DE')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => setShowAuditLog(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Schließen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SharePointFileBrowser
